import React, { useRef, useState } from 'react';
import {
	withCrossFlow,
	Journeys,
	Targets,
	type WithCrossFlowProps,
} from '@atlassiansox/cross-flow-support';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import MoreIcon from '@atlaskit/icon/glyph/more';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT } from '@atlassian/jira-feedback-collector/src/constants.tsx';
import { useFeedbackCollectorActions } from '@atlassian/jira-feedback-collector/src/controllers/index.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import {
	ContextualAnalyticsData,
	fireUIAnalytics,
	DROPDOWN,
} from '@atlassian/jira-product-analytics-bridge';
import { ResponsiveButton } from '@atlassian/jira-responsive-button/src/ui/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import { FEEDBACK_COLLECTOR_LOCATION_VALUE } from '../../../common/constant';
import commonMessages from '../../../common/messages';
import messages from './messages';

const ON_CALL_SCHEDULE_OPTOUT_SOURCE_COMPONENT = 'on-call-schedule-optout';

export type ExpandMode = 'pre' | 'post' | 'none';

type Props = {
	expandMode: ExpandMode;
	baseUrl: string;
	projectId: string | number;
	projectKey: string;
	simplified: boolean;
} & WithCrossFlowProps & {
		intl: IntlShape;
	};

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onLinkItemClick = (event: any, analyticsEvent: UIAnalyticsEvent) => {
	fireUIAnalytics(analyticsEvent, 'linkOpsgenieTeamItem');
};

export const MoreMenu = ({
	expandMode,
	baseUrl,
	projectKey,
	simplified,
	intl,
	crossFlow,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false);
	const { openFeedbackCollector } = useFeedbackCollectorActions();
	const feedbackButtonRef = useRef<HTMLButtonElement>(null);

	const onOpenChange = (
		{ isOpen: isNextOpen }: OnOpenChangeArgs,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (isOpen !== isNextOpen) {
			setIsOpen(isNextOpen);
			fireUIAnalytics(analyticsEvent, 'moreMenu');
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onOptOutClick = (event: any, analyticsEvent: UIAnalyticsEvent) => {
		crossFlow.isEnabled &&
			crossFlow.api.open({
				journey: Journeys.GET_STARTED,
				targetProduct: Targets.OPSGENIE,
				sourceComponent: ON_CALL_SCHEDULE_OPTOUT_SOURCE_COMPONENT,
				sourceContext: 'jira',
			});
		if (analyticsEvent) {
			fireUIAnalytics(analyticsEvent, ON_CALL_SCHEDULE_OPTOUT_SOURCE_COMPONENT);
		}
	};

	const createTriggerButton = () => <MoreIcon label={intl.formatMessage(messages.label)} />;

	const getPreExpandMenu = () => [
		<DropdownItemWithAnalytics key="pre-expand-menu-item" onClick={onOptOutClick}>
			{intl.formatMessage(messages.optOutOnCallSchedule)}
		</DropdownItemWithAnalytics>,
	];

	const getPostExpandMenu = () => {
		let settingsUrl = '';
		if (simplified) {
			settingsUrl = `${baseUrl}/jira/software/projects/${projectKey}/settings/opsgenie`;
		} else {
			settingsUrl = `${baseUrl}/jira/software/c/projects/${projectKey}/settings/opsgenie`;
		}

		return [
			<DropdownItemWithAnalytics
				key="post-expand-menu-item"
				onClick={onLinkItemClick}
				href={settingsUrl}
			>
				{intl.formatMessage(messages.changeLinkedTeam)}
			</DropdownItemWithAnalytics>,
		];
	};

	const onSubmitFeedbackClick = () => {
		openFeedbackCollector({
			entrypointId: ENTRYPOINT_ID_JIRA_SOFTWARE_PROJECT,
			feedbackGroupId: FEEDBACK_COLLECTOR_LOCATION_VALUE,
			triggerRef: feedbackButtonRef,
		});
	};

	const triggerButton = createTriggerButton();
	const dropdownItems = [];
	if (expandMode !== 'none') {
		dropdownItems.push(expandMode === 'pre' ? getPreExpandMenu() : getPostExpandMenu());
	}

	if (getWillShowNav4()) {
		dropdownItems.push(
			<DropdownItem onClick={onSubmitFeedbackClick}>
				{intl.formatMessage(commonMessages.leaveFeedback)}
			</DropdownItem>,
		);
	}

	return (
		<ContextualAnalyticsData sourceName="moreMenu" sourceType={DROPDOWN}>
			<DropdownMenu
				placement="bottom-end"
				// eslint-disable-next-line jira/react-no-inline-function-prop
				trigger={({ triggerRef, ...props }) => (
					<>
						{/* eslint-disable-next-line no-nested-ternary */}
						{isVisualRefreshEnabled() ? (
							<IconButton
								{...props}
								icon={ShowMoreHorizontalIcon}
								label={intl.formatMessage(messages.label)}
								ref={triggerRef}
							/>
						) : getWillShowNav4() ? (
							<ResponsiveButton
								{...props}
								label={intl.formatMessage(messages.label)}
								// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
								icon={MoreIcon}
								ref={triggerRef}
							/>
						) : (
							<Button {...props} ref={triggerRef} iconBefore={triggerButton} />
						)}
					</>
				)}
				// @ts-expect-error Type '({ isOpen }: OnOpenChangeArgs, analyticsEvent: UIAnalyticsEventInterface) => void' is not assignable to type '(args: OnOpenChangeArgs) => void'.
				onOpenChange={onOpenChange}
			>
				<DropdownItemGroup>{dropdownItems}</DropdownItemGroup>
			</DropdownMenu>
		</ContextualAnalyticsData>
	);
};

export default injectIntl(withCrossFlow(MoreMenu));
